import { useEffect } from 'react'

interface MetaTagsOptions {
  title?: string
  description?: string
  image?: string
  url?: string
  type?: string
}

export function useDynamicMetaTags(options: MetaTagsOptions) {
  useEffect(() => {
    const originalMetaTags: { [key: string]: string } = {}
    
    // Function to update or create a meta tag
    const updateMetaTag = (property: string, content: string) => {
      let metaTag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement
      
      if (!metaTag) {
        // Try name attribute if property doesn't exist
        metaTag = document.querySelector(`meta[name="${property}"]`) as HTMLMetaElement
      }
      
      if (metaTag) {
        // Store original value for cleanup
        originalMetaTags[property] = metaTag.content
        metaTag.content = content
      } else {
        // Create new meta tag
        metaTag = document.createElement('meta')
        if (property.startsWith('og:') || property.startsWith('twitter:')) {
          metaTag.setAttribute('property', property)
        } else {
          metaTag.setAttribute('name', property)
        }
        metaTag.content = content
        document.head.appendChild(metaTag)
        // Mark as created for cleanup
        originalMetaTags[property] = '__CREATED__'
      }
    }

    // Update title
    if (options.title) {
      originalMetaTags['title'] = document.title
      document.title = options.title
      updateMetaTag('og:title', options.title)
      updateMetaTag('twitter:title', options.title)
    }

    // Update description
    if (options.description) {
      updateMetaTag('description', options.description)
      updateMetaTag('og:description', options.description)
      updateMetaTag('twitter:description', options.description)
    }

    // Update image
    if (options.image) {
      updateMetaTag('og:image', options.image)
      updateMetaTag('twitter:image', options.image)
    }

    // Update URL
    if (options.url) {
      updateMetaTag('og:url', options.url)
      updateMetaTag('twitter:url', options.url)
      
      // Update canonical URL
      const canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement
      if (canonicalLink) {
        originalMetaTags['canonical'] = canonicalLink.href
        canonicalLink.href = options.url
      }
    }

    // Update type
    if (options.type) {
      updateMetaTag('og:type', options.type)
    }

    // Cleanup function to restore original meta tags
    return () => {
      Object.entries(originalMetaTags).forEach(([property, originalValue]) => {
        if (property === 'title') {
          document.title = originalValue
        } else if (property === 'canonical') {
          const canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement
          if (canonicalLink) {
            canonicalLink.href = originalValue
          }
        } else if (originalValue === '__CREATED__') {
          // Remove created meta tag
          const metaTag = document.querySelector(`meta[property="${property}"], meta[name="${property}"]`)
          if (metaTag) {
            metaTag.remove()
          }
        } else {
          // Restore original value
          const metaTag = document.querySelector(`meta[property="${property}"], meta[name="${property}"]`) as HTMLMetaElement
          if (metaTag) {
            metaTag.content = originalValue
          }
        }
      })
    }
  }, [options.title, options.description, options.image, options.url, options.type])
} 